import { useCallback } from "react";
import { AuthStateType } from "../../UserReducer";
import { useDispatch } from "./useDispatch";
import { useAuthState } from "./useAuthState";

import deviceCodeAction from "../../Actions/Auth/deviceCodeAction";
import deviceTokenAction from "../../Actions/Auth/deviceTokenAction";

import {
  fetchDiscoveryDocumentAsync,
  fetchDeviceRequestAuthorizationAsync,
  fetchRequestDeviceTokenAsync,
} from "../../../utils/signinFetches";

const useDeviceSignin = (): [AuthStateType, () => Promise<void>] => {
  const state = useAuthState();
  const dispatch = useDispatch();

  var checkTokenTimer: NodeJS.Timer;

  const signin = useCallback(async () => {
    const discoveryDocument = await fetchDiscoveryDocumentAsync();

    // Request Device code
    const deviceResponse = await fetchDeviceRequestAuthorizationAsync(
      discoveryDocument.device_authorization_endpoint
    );
    const intervalFetch = 1000 * (deviceResponse.interval ?? 1);
    dispatch(
      deviceCodeAction(
        deviceResponse.device_code,
        deviceResponse.user_code,
        deviceResponse.verification_uri,
        deviceResponse.verification_uri_complete,
        deviceResponse.expires_in,
        deviceResponse.interval
      )
    );

    clearInterval(checkTokenTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    checkTokenTimer = setInterval(async function () {
      try {
        const tokenResponse = await fetchRequestDeviceTokenAsync(
          discoveryDocument.token_endpoint,
          deviceResponse.device_code
        );
        if (tokenResponse.ok) {
          const json = await tokenResponse.json();
          dispatch(
            deviceTokenAction(
              json.id_token,
              json.access_token,
              json.refresh_token,
              json.expires_in
            )
          );
          // Close sub window opened for authentication
          // window?.electronAPI?.closeSubWindow();
          clearInterval(checkTokenTimer);
        }
      } catch (error) {
        console.log(error);
      }
    }, intervalFetch);
  }, []);

  return [state, signin];
};

const getVerificationUriComplete = async (): Promise<{
  tokenEndpoint: string;
  deviceCode: string;
  verificationUriComplete: string;
}> => {
  const discoveryDocument = await fetchDiscoveryDocumentAsync();
  // Request Device code
  const deviceResponse = await fetchDeviceRequestAuthorizationAsync(
    discoveryDocument.device_authorization_endpoint
  );

  return {
    tokenEndpoint: discoveryDocument.token_endpoint,
    deviceCode: deviceResponse.device_code,
    verificationUriComplete: deviceResponse.verification_uri_complete,
  };
};

const signinForParameters = async (
  tokenEndpoint: string,
  deviceCode: string
): Promise<boolean> => {
  try {
    const tokenResponse = await fetchRequestDeviceTokenAsync(
      tokenEndpoint,
      deviceCode
    );
    if (tokenResponse.ok) {
      return true;
    }
  } catch (error) {
    console.log(error);
    return false;
  }

  return false;
};

export default useDeviceSignin;
export { getVerificationUriComplete, signinForParameters };
